import { ProductFeature, ProductFeatureStatus } from "components/product/product.types";
import { PlanProps } from "shared/components/plan/plan";
import { LIBUM_DOCS_POWERON_PRO, LIBUM_PORTAL, POWERON_PRO_INSTALL_URL } from "shared/constants/globals";
import { PlanOfferings } from "shared/constants/plans";
import { createItems } from "shared/constants/utilities";

export const POWERON_PRO_FEATURES: ProductFeature[] = [
  {
    id: "ondemand-rendering",
    name: "OnDemand in VS Code",
    description: "Run PowerOns with member selection, interactive prompts, and rendered output right in VS Code.",
    isFeatured: true,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#on-demand-poweron-rendering`,
  },
  {
    id: "poweron-diagnostics",
    name: "Diagnostics as You Type",
    description: "Catch errors as you type with local analysis and quick fixes. No on-host validation required.",
    isFeatured: true,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#poweron-diagnostics`,
  },
  {
    id: "remote-syms",
    name: "Remote Syms & Change Review",
    description: "Work locally, review remote changes in Source Control, and choose what to keep before syncing.",
    isFeatured: true,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#remote-syms`,
  },
  {
    id: "batch-reports",
    name: "Batch Jobs & Reports",
    description: "Run, cancel, or terminate batch jobs. Find, view, export, and print reports in VS Code.",
    isFeatured: true,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
  },
  {
    id: "symitar-integration-ssh",
    name: "Native Symitar Connection",
    description: "Work directly with Symitar from VS Code—PowerOns, files, batch jobs, reports, and more.",
    isFeatured: false,
    isHidden: false,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#direct-integration-to-symitar-ssh`,
  },
  {
    id: "poweron-code-formatter",
    name: "Consistent PowerOn Formatting",
    description: "Keep your PowerOns readable with configurable indentation, keyword casing, and spacing.",
    isFeatured: false,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#poweron-code-formatter`,
  },
  {
    id: "edocs-hovers",
    name: "eDocs at Your Cursor",
    description: "Look up keywords, functions, and database records without leaving your code.",
    isFeatured: false,
    isHidden: false,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#hovers`,
  },
  {
    id: "embedded-html-support",
    name: "PX · Embedded HTML",
    description: "Extended language support for embedded HTML. Coming in a follow-up minor release.",
    isFeatured: false,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.PLANNED,
  },
  {
    id: "job-files-manager",
    name: "Job File Manager",
    description: "Manage job files from VS Code. Coming in a follow-up minor release.",
    isFeatured: false,
    isHidden: false,
    offeringType: "Plus",
    imagePath: "",
    status: ProductFeatureStatus.PLANNED,
  },
  {
    id: "syntax-highlighting",
    name: "Syntax Highlighting",
    description: "Purpose-built highlighting for the PowerOn language.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#syntax-highlighting`,
  },
  {
    id: "code-completions",
    name: "Code Completions & Signatures",
    description: "Get relevant completions and function signatures as you write.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#code-completions`,
  },
  {
    id: "definitions-and-references",
    name: "Definitions & References",
    description: "Navigate definitions, references, and dependencies across your PowerOns and include files.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#multi-document-definitions-and-references`,
  },
  {
    id: "code-snippets",
    name: "Code Snippets",
    description: "Get started faster with an extensible snippet library.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#code-snippets`,
  },
  {
    id: "file-icon-theme",
    name: "File Icon Theme",
    description: "Recognize PowerOn files with an extended Seti icon theme.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#file-icon-theme`,
  },
  {
    id: "poweron-tasks",
    name: "PowerOn Tasks",
    description: "Keep track of your PowerOn to-dos inside the editor.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#tasks`,
  },
  {
    id: "clone-sym-directory",
    name: "Clone Sym Directory",
    description: "Download a Sym directory into an empty local folder to start a local workspace.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#clone-sym-directory`,
  },
  {
    id: "auto-deploy-includes",
    name: "Auto-Deploy Include Files",
    description: "Manage include deployments with options to prompt, automatically deploy, or skip.",
    isFeatured: false,
    isHidden: true,
    offeringType: "Free",
    imagePath: "",
    status: ProductFeatureStatus.AVAILABLE,
    docsLink: `${LIBUM_DOCS_POWERON_PRO}/capabilities#auto-deploy-include-files`,
  },
];

export const POWERON_PRO_PLAN_FEATURES = [
  {
    name: "Symitar connections",
    offerings: [
      { name: PlanOfferings.POWERON_PRO, description: "1 connection" },
      { name: PlanOfferings.POWERON_PRO_PLUS, description: "Unlimited" },
    ],
  },
  {
    name: "Syntax highlighting, completions, signatures, snippets, and eDocs hovers",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Go to definition, find references, and rename",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "File icons and inline to-dos",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "On-host PowerOn validation, deployment, and installation",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Auto-deploy include files",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "As-you-type local diagnostics and quick fixes",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Reference CodeLens",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "PowerOn code formatting",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Remote Syms with Source Control change review",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Clone Sym directories to a local workspace",
    offerings: [
      { name: PlanOfferings.POWERON_PRO },
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Interactive OnDemand execution",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Batch execution, queue cancellation, and termination requests",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: "Reports: search, view, export, and print",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS },
    ],
  },
  {
    name: '<span class="feature-label">PX<small>Embedded HTML</small></span>',
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS, description: "Coming soon" },
    ],
  },
  {
    name: "Job File Manager",
    offerings: [
      { name: PlanOfferings.POWERON_PRO_PLUS, description: "Coming soon" },
    ],
  },
];
export const POWERON_PRO_PLAN_FEATURES_MAP = createItems(POWERON_PRO_PLAN_FEATURES, 'poweron-pro');

export const POWERON_PRO_PLAN: PlanProps = {
  header: 'Use PowerOn Pro for free, or subscribe to next-level features',
  features: POWERON_PRO_PLAN_FEATURES_MAP,
  offerings: [{
    name: PlanOfferings.POWERON_PRO,
    id: 'offering-poweron-pro',
    class: 'poweron-pro success',
    link: {
      href: POWERON_PRO_INSTALL_URL,
      text: 'Install Now',
    }
  }, {
    name: PlanOfferings.POWERON_PRO_PLUS,
    id: 'offering-poweron-pro-plus',
    class: 'poweron-pro',
    link: {
      href: LIBUM_PORTAL,
      text: 'Subscribe',
    }
  }]
}

export const POWERON_PRO_WORKSPACE_IMAGE = {
  light: "/images/poweron-pro/v2/workspace-light.webp",
  dark: "/images/poweron-pro/v2/workspace-dark.webp",
  width: 1600,
  height: 982,
};
